import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Trophy, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    cricketRole: 'Player',
    cricclubsId: ''
  });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.fullName) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(
      formData.email,
      formData.password,
      formData.fullName,
      formData.cricketRole,
      formData.cricclubsId || null
    );
    setLoading(false);

    if (result.success) {
      toast.success('Account created! Welcome to Cricket Refreshed');
      
      // Route based on role
      if (formData.cricketRole === 'Coach') {
        navigate('/coach-dashboard');
      } else {
        navigate('/player-dashboard');
      }
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <Card className={`glass-card max-w-md w-full mx-4 ${theme === 'light' ? 'bg-white/95' : ''}`}>
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Join Cricket Refreshed</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Start your cricket development journey</p>
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Full Name *
            </label>
            <div className="relative">
              <User className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <Input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                placeholder="John Smith"
                className={`pl-10 ${theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                data-testid="register-name"
              />
            </div>
          </div>

          <div>
            <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Email *
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="your@email.com"
                className={`pl-10 ${theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                data-testid="register-email"
              />
            </div>
          </div>

          <div>
            <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Password *
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Minimum 6 characters"
                className={`pl-10 ${theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                data-testid="register-password"
              />
            </div>
          </div>

          <div>
            <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              I am a...
            </label>
            <Select 
              value={formData.cricketRole} 
              onValueChange={(value) => setFormData({...formData, cricketRole: value})}
            >
              <SelectTrigger className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Player">Player</SelectItem>
                <SelectItem value="Coach">Coach</SelectItem>
                <SelectItem value="Academy Owner">Academy Owner</SelectItem>
                <SelectItem value="Fan">Fan</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className={`text-sm font-medium mb-2 block ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              CricClubs ID (Optional)
            </label>
            <div className="relative">
              <Hash className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <Input
                type="text"
                value={formData.cricclubsId}
                onChange={(e) => setFormData({...formData, cricclubsId: e.target.value})}
                placeholder="Your CricClubs player ID"
                className={`pl-10 ${theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                data-testid="register-cricclubs"
              />
            </div>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
              We'll import your public stats from CricClubs
            </p>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            data-testid="register-button"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-green-500 hover:text-green-400 font-medium"
            >
              Login
            </button>
          </p>
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/')}
            className={`text-sm ${theme === 'dark' ? 'text-gray-500 hover:text-gray-400' : 'text-gray-600 hover:text-gray-700'}`}
          >
            Back to Home
          </button>
        </div>
      </Card>
    </div>
  );
}
